package com.nonopn.skydome.common.app.pages.dashboard.components

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.nonopn.skydome.common.app.api.model.DashboardImpactReportV2
import kotlin.math.roundToInt
import com.nonopn.skywatcher.common.app.res.Res
import com.nonopn.skywatcher.common.app.res.intensity_cg_negative_arrow
import com.nonopn.skywatcher.common.app.res.intensity_cg_positive_arrow
import com.nonopn.skywatcher.common.app.res.intensity_distribution_title
import org.jetbrains.compose.resources.stringResource

@Composable
fun IntensitiesReport(
    modifier: Modifier,
    total: DashboardImpactReportV2.NegPosCount,
    intensities: Map<DashboardImpactReportV2.IntensityDistributions, DashboardImpactReportV2.NegPosCount>
) {
    InternalCard(
        modifier = modifier,
        title = stringResource(Res.string.intensity_distribution_title)
    ) {
        Row {
            Text(
                text = stringResource(Res.string.intensity_cg_negative_arrow),
                color = DashboardColors.negativeColor,
                fontSize = 12.sp,
            )

            Spacer(modifier = Modifier.weight(1.0f))

            Text(
                text = stringResource(Res.string.intensity_cg_positive_arrow),
                color = DashboardColors.positiveColor,
                fontSize = 12.sp,
            )
        }

        intensities.forEach { (key, value) ->
            DrawIntensityLine(
                modifier.fillMaxWidth(),
                line = key,
                negPostCount = value,
                total = total.total()
            )
        }
    }
}

@Composable
private fun DrawIntensityLine(
    modifier: Modifier,
    line: DashboardImpactReportV2.IntensityDistributions,
    negPostCount: DashboardImpactReportV2.NegPosCount,
    total: Int,
) {
    Row(
        modifier = modifier,
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        DrawLinePart(
            modifier = Modifier.weight(1.0f),
            isNegative = true,
            count = negPostCount.negative,
            total = total
        )

        Column(
            modifier = Modifier.width(80.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Text(
                text = line.text,
                color = DashboardColors.titleColor,
                fontSize = 12.sp,
            )

            val toPercent = if (total > 0) {
                (negPostCount.total() * 100.0 / total).roundToInt()
            } else {
                0
            }

            Text(
                "$toPercent %",
                fontWeight = FontWeight.Bold,
                color = DashboardColors.titleColor,
                fontSize = 13.sp,
            )
        }

        DrawLinePart(
            modifier = Modifier.weight(1.0f),
            isNegative = false,
            count = negPostCount.positive,
            total = total
        )
    }
}

@Composable
private fun DrawLinePart(
    modifier: Modifier,
    isNegative: Boolean,
    count: Int,
    total: Int,
) {
    Row(
        modifier = modifier,
        horizontalArrangement = when (isNegative) {
            true -> Arrangement.spacedBy(4.dp, Alignment.End)
            false -> Arrangement.spacedBy(4.dp, Alignment.Start)
        },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        val color = when (isNegative) {
            true -> DashboardColors.negativeColor
            false -> DashboardColors.positiveColor
        }

        if (isNegative) {
            Text(
                text = "$count",
                color = color,
                fontWeight = FontWeight.Bold,
                fontSize = 13.sp,
            )
        }

        Spacer(
            modifier = Modifier.width(100.dp * (count * 1.0f / total))
                .height(20.dp)
                .clip(RoundedCornerShape(5.dp))
                .background(color),
        )

        if (!isNegative) {
            Text(
                text = "$count",
                color = color,
                fontWeight = FontWeight.Bold,
                fontSize = 13.sp,
            )
        }
    }
}


@Preview
@Composable
fun IntensitiesReportPreview() {
    val map =
        DashboardImpactReportV2.IntensityDistributions.entries.mapIndexed { index, distributions ->
            distributions to DashboardImpactReportV2.NegPosCount(
                index * 10, 2 * index * index * 10
            )
        }.toMap()

    IntensitiesReport(
        modifier = Modifier.width(600.dp),
        intensities = map,
        total = map.values.let {
            DashboardImpactReportV2.NegPosCount(
                positive = it.sumOf { holder -> holder.positive },
                negative = it.sumOf { holder -> holder.negative },
            )
        }
    )
}
